import React from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { LayoutDashboard, Search, Sparkles, Bookmark, ListChecks, KanbanSquare, Mail, Upload, Coins, CreditCard, Users, Settings, Shield, LogOut, Loader2, Database } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { tk } from "@/i18n";
import { Logo, LangToggle, ThemeToggle } from "@/components/common";
import { UpgradeModal } from "@/components/UpgradeModal";
import { NotificationBell } from "@/components/NotificationBell";
import { isApproved } from "@/components/Approval";
import { InsufficientCreditsModal } from "@/components/CreditPacks";

const NAV = [
  ["dashboard", "/app", LayoutDashboard, tk("Dashboard")], ["talent_search", "/app/search", Search, tk("Talent Search")], ["ai_discovery", "/app/discovery", Sparkles, tk("AI Discovery")], ["pool", "/app/pool", Database, tk("Talent pool")],
  ["saved_searches", "/app/saved", Bookmark, tk("Saved Searches")], ["talent_lists", "/app/lists", ListChecks, tk("Talent Lists")], ["projects", "/app/projects", KanbanSquare, tk("Projects")],
  ["messages", "/app/messages", Mail, tk("Messages")], ["imports", "/app/imports", Upload, tk("Imports")], ["credits", "/app/credits", Coins, tk("AI Credits")],
  ["billing", "/app/billing", CreditCard, tk("Billing")], ["team", "/app/team", Users, tk("Team")], ["settings", "/app/settings", Settings, tk("Settings")],
];

export function CreditPill() {
  const { user, setUpgradeOpen } = useAuth();
  const { t } = useI18n();
  const ent = user?.entitlements;
  const navigate = useNavigate();
  if (!ent) return null;
  return (
    <div className="flex items-center gap-2">
      {!ent.is_paid && (
        <button onClick={() => setUpgradeOpen(true)} data-testid="trial-badge"
          className="hidden sm:inline-flex h-8 items-center rounded-full px-3 text-xs font-semibold bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200 hover:bg-amber-100 transition-colors">
          {ent.trial_active ? t("Trial · {{count}} days left", { count: ent.trial_days_left }) : t("Trial expired")} · {t("Upgrade")}
        </button>
      )}
      <button onClick={() => navigate("/app/credits")} data-testid="credit-balance-pill"
        className="h-8 inline-flex items-center gap-1.5 rounded-full border px-3 text-xs font-semibold hover:border-primary/50 transition-colors">
        <Sparkles className="w-3.5 h-3.5 text-primary" />
        <span className="font-mono">{user.wallet?.balance ?? 0}</span><span className="text-muted-foreground">{t("credits")}</span>
      </button>
    </div>
  );
}

export function RequireAuth({ type, admin, children }) {
  const { user } = useAuth();
  if (user === null) return <div className="h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isApproved(user)) return <Navigate to="/pending" replace />;
  if (admin && user.platform_role !== "platform_admin") return <Navigate to="/app" replace />;
  if (type && user.account_type !== type) return <Navigate to={user.account_type === "candidate" ? "/me" : "/app"} replace />;
  return children;
}

export default function AppShell() {
  const { user, signOut } = useAuth();
  const { t } = useI18n();
  const noOrg = !user.memberships?.length;
  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r bg-card/60 backdrop-blur sticky top-0 h-screen" data-testid="app-sidebar">
        <div className="h-14 flex items-center px-4 border-b"><Logo to="/app" /></div>
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {NAV.map(([k, to, Icon, label]) => (
            <NavLink key={k} to={to} end={to === "/app"} data-testid={`nav-${k}`}
              className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors ${isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}>
              <Icon className="w-4 h-4" />{t(label)}
            </NavLink>
          ))}
          {user.platform_role === "platform_admin" && (
            <NavLink to="/admin" data-testid="nav-admin" className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted">
              <Shield className="w-4 h-4" />{t("Admin")}
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t">
          <div className="text-xs font-semibold truncate" data-testid="sidebar-org-name">{user.memberships?.[0]?.organization_name || t("No organization")}</div>
          <div className="text-[11px] text-muted-foreground truncate">{user.email} · {t(`role.${user.memberships?.[0]?.role}`, { defaultValue: user.memberships?.[0]?.role || "" })}</div>
          <button onClick={signOut} data-testid="logout-button" className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"><LogOut className="w-3.5 h-3.5" />{t("Sign out")}</button>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 border-b bg-background/80 backdrop-blur sticky top-0 z-30 flex items-center justify-between px-4 gap-2">
          <div className="lg:hidden"><Logo to="/app" small /></div>
          <nav className="lg:hidden flex gap-1 overflow-x-auto">
            {NAV.slice(0, 6).map(([k, to, Icon, label]) => <NavLink key={k} to={to} end={to === "/app"} aria-label={t(label)} className="p-2 rounded-md text-muted-foreground" data-testid={`mnav-${k}`}><Icon className="w-4 h-4" /></NavLink>)}
          </nav>
          <div className="flex-1" />
          <CreditPill />
          {!noOrg && <NotificationBell />}
          <LangToggle /><ThemeToggle />
        </header>
        <main className="flex-1 min-w-0">{noOrg ? <div className="p-8 text-sm" data-testid="no-org">{t("Your account is not a member of an organization. Ask an admin to add you.")}</div> : <Outlet />}</main>
      </div>
      <UpgradeModal />
      <InsufficientCreditsModal />
    </div>
  );
}
